import { NavLink } from "react-router-dom";
import { X, MoreHorizontal, MessageSquare, Home, ChevronDown, ChevronsLeft, ChevronsRight } from "lucide-react";
import { MessageBadge } from "./MessageBadge";
import { AttentionBadge } from "./AttentionBadge";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS } from "../lib/bookingStatus";

// 20 Jun 2026 · Datei-Split von Layout.jsx. Navigations-/Sidebar-Chrome
// ausgelagert (Brand, Mobile-„Mehr"-Menü, Kunden-Bottom-Nav + die drei
// Render-Blöcke Kunden-Nav-Zeile / Team-Mobile-Nav / Team-Sidebar). Reine
// präsentationale Komponenten — Props tragen exakt die bisherigen lokalen
// Variablennamen, Markup 1:1 unverändert.

function Brand({ compact, to }) {
  // Feb 2026 · Weissblom-Logo ist klickbar — führt Team-User zurück zu
  // ihrer Landing-Seite (`/team`) und Kunden zu `/app`. Damit hat der
  // Concierge auch ohne Team-Übersicht-Menüpunkt einen klaren Weg zurück
  // zum ConciergeHome (mit Anreise-Radar + Objekte-Kurzliste).
  const inner = (
    <>
      <img
        src="/weissblom-logo.webp"
        alt="Weissblom"
        className={compact ? "h-9 w-auto" : "h-11 w-auto"}
        style={{ objectFit: "contain" }}
      />
    </>
  );
  if (!to) {
    return (
      <div className="flex items-center gap-3" data-testid="brand">
        {inner}
      </div>
    );
  }
  return (
    <NavLink
      to={to}
      className="flex items-center gap-3 hover:opacity-80 transition-opacity"
      data-testid="brand"
      aria-label="Zur Startseite"
    >
      {inner}
    </NavLink>
  );
}

function MobileMoreMenu({ open, onClose, links, onLogout, viewOptions, activeView, onSwitchView }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-more-menu">
      <div className="absolute inset-0 bg-navy/70" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-[360px] bg-ivory shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy/10 bg-navy text-ivory" style={{ background: BRAND_COLORS.navy }}>
          <div className="label-caps text-ivory">Weissblom</div>
          <button onClick={onClose} data-testid="mobile-more-close" aria-label="Close">
            <X className="w-5 h-5 text-ivory" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {links.filter((l) => !l.inMore && l.id !== "nav-cust-dashboard" && l.id !== "nav-cust-chat").map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={onClose}
              data-testid={`${l.id}-mobile`}
              className={({ isActive }) =>
                `block px-4 py-3 text-[15px] rounded-md ${isActive ? "bg-gold/10 text-navy border-l-2 border-gold" : "text-navy-dark hover:bg-navy/5"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {links.some((l) => l.inMore) && (
            <div className="mt-4 ml-3 pl-3 border-l border-navy/15" data-testid="mobile-more-group">
              <div className="label-caps text-[10px] text-navy-dark/45 px-4 pt-1 pb-1" data-testid="mobile-more-group-label">Mehr</div>
              {links.filter((l) => l.inMore).map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={onClose}
                  data-testid={`${l.id}-mobile`}
                  className={({ isActive }) =>
                    `block px-4 py-2.5 text-[14px] rounded-md ${isActive ? "bg-gold/10 text-navy border-l-2 border-gold" : "text-navy-dark/80 hover:bg-navy/5"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>
        <div className="border-t border-navy/10 p-4 space-y-3">
          {viewOptions.length > 1 && (
            <div
              role="group"
              aria-label="Ansicht wechseln"
              data-testid="mobile-view-switcher"
              className="flex flex-col gap-1"
            >
              <div className="label-caps text-[10px] text-navy-dark/60 mb-1">Ansicht</div>
              {viewOptions.map((opt) => {
                const active = opt.key === activeView;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => { onClose(); onSwitchView(opt.key); }}
                    data-testid={`mobile-view-switcher-${opt.key}`}
                    aria-pressed={active}
                    className={`block w-full text-left text-[12px] tracking-[0.2em] uppercase px-4 py-2.5 border transition-colors ${
                      active
                        ? "bg-navy text-ivory border-navy"
                        : "bg-white text-navy border-navy/25 hover:border-navy hover:bg-navy/5"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
          <button
            onClick={onLogout}
            data-testid="mobile-logout-btn"
            className="block w-full text-left text-[12px] tracking-[0.2em] uppercase text-navy-dark/70 hover:text-navy py-2"
          >
            Abmelden
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomerBottomNav({ onOpenMore, unreadMessages, newsCount = 0 }) {
  const { lang } = useI18n();
  const item = "flex flex-col items-center gap-1 min-w-[60px] py-2";
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-ivory border-t border-navy/10 flex items-end justify-around px-2 pt-2 pb-[env(safe-area-inset-bottom)]"
      data-testid="mobile-bottom-nav"
    >
      {/* Feb 2026 · Bottom-Nav Rev 6 (27 Feb 2026): Übersicht-FAB immer
          exakt in der Mitte, unabhängig von der Anzahl der Nachbar-Items.
          Wir nehmen den FAB aus dem Flow (absolute + left-1/2 + translate)
          und lassen die restlichen Nav-Items mit `justify-around`
          symmetrisch um den zentralen Punkt herum flowen. Nachrichten
          rutscht links, Mehr rechts — die genaue Verteilung passt sich
          automatisch an, egal wie viele Slots wir künftig ergänzen. */}
      <NavLink to="/app/chat" data-testid="botnav-messages"
        className={({ isActive }) => `${item} ${isActive ? "text-navy" : "text-navy-dark/50"}`}>
        {({ isActive }) => (<>
          <span className="relative inline-flex">
            <MessageSquare className="w-5 h-5" strokeWidth={1.6} />
            <MessageBadge count={unreadMessages} testid="botnav-messages-badge" small />
          </span>
          <span className={`text-[10px] tracking-wider ${isActive ? "text-navy font-medium" : ""}`}>{lang === "en" ? "Messages" : "Nachrichten"}</span>
        </>)}
      </NavLink>
      <NavLink
        to="/app" end data-testid="botnav-dashboard"
        className="absolute left-1/2 bottom-[env(safe-area-inset-bottom)] -translate-x-1/2 flex flex-col items-center -mt-6 min-w-[60px] py-2 pointer-events-auto"
      >
        {({ isActive }) => (<>
          <span className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-md" style={{ background: BRAND_COLORS.navy }}>
            <Home className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.5} />
            {newsCount > 0 && (
              <span
                data-testid="botnav-news-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center leading-none"
                style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.navy, boxShadow: `0 0 0 2px ${BRAND_COLORS.ivory}` }}
                aria-label={lang === "en" ? "New updates" : "Neuigkeiten"}
              >
                {newsCount > 99 ? "99+" : newsCount}
              </span>
            )}
          </span>
          <span className={`text-[10px] tracking-wider mt-1 ${isActive ? "text-navy font-medium border-b border-navy" : "text-navy-dark/70"}`}>{lang === "en" ? "Overview" : "Übersicht"}</span>
        </>)}
      </NavLink>
      <button
        type="button"
        onClick={onOpenMore}
        data-testid="botnav-more"
        className={`${item} text-navy-dark/50`}
      >
        <MoreHorizontal className="w-5 h-5" strokeWidth={1.6} />
        <span className="text-[10px] tracking-wider">{lang === "en" ? "More" : "Mehr"}</span>
      </button>
    </nav>
  );
}

function CustomerNavRow({ links, unreadMessages, custMoreOpen, setCustMoreOpen, lang, scrolled }) {
  return (
          <div className="hidden md:block border-t border-ivory/10">
            <nav
              data-testid="customer-nav-row"
              className={`max-w-[1400px] mx-auto px-5 md:px-10 flex items-center gap-1 whitespace-nowrap transition-[padding] duration-200 ${
                scrolled ? "py-1" : "py-2"
              }`}
            >
              {links.filter((l) => !l.inMore).map((l) => {
                const isChatLink = l.id === "nav-cust-chat" || l.id === "nav-team-chat";
                return (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    data-testid={l.id}
                    className={({ isActive }) =>
                      `relative px-4 py-2 text-[13px] tracking-wider transition-colors inline-flex items-center ${
                        isActive ? "text-gold border-b border-gold" : "text-ivory/80 hover:text-ivory border-b border-transparent"
                      }`
                    }
                  >
                    {l.label}
                    {isChatLink && <MessageBadge count={unreadMessages} testid={`${l.id}-badge`} inline />}
                  </NavLink>
                );
              })}
              {links.some((l) => l.inMore) && (
                <div className="relative inline-flex">
                  <button
                    type="button"
                    data-testid="nav-cust-more"
                    onClick={() => setCustMoreOpen((v) => !v)}
                    aria-expanded={custMoreOpen}
                    className={`relative px-4 py-2 text-[13px] tracking-wider transition-colors inline-flex items-center gap-1 border-b border-transparent ${
                      custMoreOpen ? "text-gold" : "text-ivory/80 hover:text-ivory"
                    }`}
                  >
                    {lang === "en" ? "More" : "Mehr"}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${custMoreOpen ? "rotate-180" : ""}`} strokeWidth={1.8} />
                  </button>
                  {custMoreOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setCustMoreOpen(false)} />
                      <div
                        data-testid="customer-more-dropdown"
                        className="absolute right-0 top-full mt-1 z-50 bg-white border border-navy/10 shadow-xl min-w-[240px] py-2"
                      >
                        {links.filter((l) => l.inMore).map((l) => (
                          <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            data-testid={`${l.id}-more`}
                            onClick={() => setCustMoreOpen(false)}
                            className={({ isActive }) =>
                              `block px-4 py-2.5 text-[13px] tracking-wide transition-colors ${
                                isActive ? "text-gold bg-gold/5" : "text-navy-dark hover:bg-navy/5"
                              }`
                            }
                          >
                            {l.label}
                          </NavLink>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </nav>
          </div>
  );
}

function TeamMobileNav({ links, unreadMessages, unreadTeamQuotes, attention, scrolled }) {
  return (
          <div className={`md:hidden overflow-x-auto border-t border-ivory/10 max-w-full transition-[padding] duration-200`}>
            <div className={`flex px-4 gap-4 whitespace-nowrap w-max ${scrolled ? "py-1" : "py-2"}`}>
              {links.map((l) => {
                const isChatLink = l.id === "nav-cust-chat" || l.id === "nav-team-chat";
                const isTeamQuotesLink = l.id === "nav-team-quotes";
                const attentionCount = l.id === "nav-team-requests"
                  ? attention.requests
                  : l.id === "nav-team-properties"
                    ? attention.properties
                    : l.id === "nav-team-bookings"
                      ? (attention.overdue_bookings ?? 0)
                      : l.id === "nav-team-projects"
                        ? (attention.projects_red ?? 0)
                        : 0;
                const attentionTo = l.id === "nav-team-requests"
                  ? "/team/requests?filter=urgent"
                  : l.id === "nav-team-properties"
                    ? "/team/properties?filter=attention"
                    : l.id === "nav-team-bookings"
                      ? "/team/bookings?mode=overdue"
                      : l.id === "nav-team-projects"
                    ? "/team/projects?color=red"
                        : undefined;
                const blueCount = l.id === "nav-team-projects"
                  ? (attention.projects_blue ?? 0)
                  : 0;
                const blueTo = l.id === "nav-team-projects"
                  ? "/team/projects?color=blue"
                  : undefined;
                const goldCount = l.id === "nav-team-requests"
                  ? (attention.unseen_requests ?? 0)
                  : l.id === "nav-team-properties"
                    ? (attention.unseen_properties ?? 0)
                    : l.id === "nav-team-bookings"
                      ? (attention.unseen_bookings ?? 0)
                      : l.id === "nav-team-projects"
                        ? (attention.unseen_projects ?? 0)
                        : 0;
                return (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    data-testid={`${l.id}-mobile`}
                    className={({ isActive }) =>
                      `relative inline-flex items-center text-[12px] tracking-wider py-1 ${isActive ? "text-gold border-b border-gold" : "text-ivory/80"}`
                    }
                  >
                    {l.label}
                    {isChatLink && <MessageBadge count={unreadMessages} testid={`${l.id}-mobile-badge`} inline />}
                    {isTeamQuotesLink && unreadTeamQuotes > 0 && (
                      <AttentionBadge count={unreadTeamQuotes} to="/team/quotes" testid={`${l.id}-mobile-badge`} inline />
                    )}
                    {attentionCount > 0 && (
                      <AttentionBadge count={attentionCount} to={attentionTo} testid={`${l.id}-mobile-attention-badge`} inline />
                    )}
                    {blueCount > 0 && (
                      <AttentionBadge count={blueCount} to={blueTo} testid={`${l.id}-mobile-blue-badge`} inline variant="blue" />
                    )}
                    {goldCount > 0 && (
                      <AttentionBadge count={goldCount} to={`${l.to}?unseen=1`} testid={`${l.id}-mobile-gold-badge`} inline variant="gold" />
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
  );
}

function TeamSidebar({ links, sidebarCollapsed, setSidebarCollapsed, sidebarWidth, unreadMessages, unreadTeamQuotes, attention }) {
  return (
        <aside
          data-testid="team-sidebar"
          data-collapsed={sidebarCollapsed ? "true" : "false"}
          className="hidden md:flex flex-col fixed left-0 bottom-0 z-30 bg-ivory border-r border-navy/10 overflow-y-auto transition-[width] duration-200"
          style={{ top: "var(--wb-header-h, 96px)", width: sidebarWidth }}
        >
          {/* Toggle-Knopf oben rechts der Sidebar */}
          <button
            type="button"
            onClick={() => setSidebarCollapsed((v) => !v)}
            data-testid="team-sidebar-toggle"
            title={sidebarCollapsed ? "Sidebar ausklappen" : "Sidebar einklappen"}
            className="self-end m-2 w-8 h-8 flex items-center justify-center text-navy-dark/60 hover:text-navy hover:bg-white/60 border border-navy/10 rounded transition-colors"
          >
            {sidebarCollapsed
              ? <ChevronsRight className="w-4 h-4" strokeWidth={1.8} />
              : <ChevronsLeft className="w-4 h-4" strokeWidth={1.8} />}
          </button>
          {!sidebarCollapsed && (
            <div className="px-5 pt-1 pb-3 label-caps text-navy-dark/50">Weissblom · Team</div>
          )}
          <nav className="flex-1 flex flex-col px-2 pb-6">
            {links.map((l) => {
              const isChatLink = l.id === "nav-team-chat";
              const isTeamQuotesLink = l.id === "nav-team-quotes";
              const attentionCount = l.id === "nav-team-requests"
                ? attention.requests
                : l.id === "nav-team-properties"
                  ? attention.properties
                  : l.id === "nav-team-bookings"
                    ? (attention.overdue_bookings ?? 0)
                    : l.id === "nav-team-projects"
                      ? (attention.projects_red ?? 0)
                      : 0;
              const attentionTo = l.id === "nav-team-requests"
                ? "/team/requests?filter=urgent"
                : l.id === "nav-team-properties"
                  ? "/team/properties?filter=attention"
                  : l.id === "nav-team-bookings"
                    ? "/team/bookings?mode=overdue"
                    : l.id === "nav-team-projects"
                      ? "/team/projects?color=red"
                      : undefined;
              // Feb 2026 · 10 Feb 2026 — Zweiter (blauer) Badge nur an
              // „Projekte": Anzahl der „In Bearbeitung"-Projekte. Klick
              // filtert die Projektliste über den bestehenden `?color=blue`-
              // Query-Param (siehe TeamProjects.jsx `colorFilter`).
              const blueCount = l.id === "nav-team-projects"
                ? (attention.projects_blue ?? 0)
                : 0;
              const blueTo = l.id === "nav-team-projects"
                ? "/team/projects?color=blue"
                : undefined;
              const goldCount = l.id === "nav-team-requests"
                ? (attention.unseen_requests ?? 0)
                : l.id === "nav-team-properties"
                  ? (attention.unseen_properties ?? 0)
                  : l.id === "nav-team-bookings"
                    ? (attention.unseen_bookings ?? 0)
                    : l.id === "nav-team-projects"
                      ? (attention.unseen_projects ?? 0)
                      : 0;
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  data-testid={`${l.id}-side`}
                  title={sidebarCollapsed ? l.label : undefined}
                  className={({ isActive }) =>
                    `relative flex items-center gap-3 py-2.5 text-[13px] tracking-wider transition-colors ${
                      sidebarCollapsed ? "px-0 justify-center" : "px-4"
                    } ${
                      isActive
                        ? "text-navy bg-white border-l-2 border-gold font-medium"
                        : "text-navy-dark/70 hover:text-navy hover:bg-white/60 border-l-2 border-transparent"
                    }`
                  }
                >
                  {Icon && <Icon className="w-4 h-4 shrink-0" strokeWidth={1.6} />}
                  {!sidebarCollapsed && <span className="flex-1">{l.label}</span>}
                  {isChatLink && !sidebarCollapsed && (
                    <MessageBadge count={unreadMessages} testid="nav-team-chat-side-badge" inline />
                  )}
                  {isChatLink && sidebarCollapsed && unreadMessages > 0 && (
                    <MessageBadge count={unreadMessages} testid="nav-team-chat-side-badge-icon" small />
                  )}
                  {isTeamQuotesLink && !sidebarCollapsed && unreadTeamQuotes > 0 && (
                    <AttentionBadge count={unreadTeamQuotes} to="/team/quotes" testid="nav-team-quotes-side-badge" inline />
                  )}
                  {isTeamQuotesLink && sidebarCollapsed && unreadTeamQuotes > 0 && (
                    <AttentionBadge count={unreadTeamQuotes} to="/team/quotes" testid="nav-team-quotes-side-badge-icon" small />
                  )}
                  {attentionCount > 0 && !sidebarCollapsed && (
                    <AttentionBadge count={attentionCount} to={attentionTo} testid={`${l.id}-side-attention-badge`} inline />
                  )}
                  {attentionCount > 0 && sidebarCollapsed && (
                    <AttentionBadge count={attentionCount} to={attentionTo} testid={`${l.id}-side-attention-badge-icon`} small />
                  )}
                  {blueCount > 0 && !sidebarCollapsed && (
                    <AttentionBadge count={blueCount} to={blueTo} testid={`${l.id}-side-blue-badge`} inline variant="blue" />
                  )}
                  {blueCount > 0 && sidebarCollapsed && (
                    <AttentionBadge count={blueCount} to={blueTo} testid={`${l.id}-side-blue-badge-icon`} small variant="blue" />
                  )}
                  {goldCount > 0 && !sidebarCollapsed && (
                    <AttentionBadge count={goldCount} to={`${l.to}?unseen=1`} testid={`${l.id}-side-gold-badge`} inline variant="gold" />
                  )}
                  {goldCount > 0 && sidebarCollapsed && (
                    <AttentionBadge count={goldCount} to={`${l.to}?unseen=1`} testid={`${l.id}-side-gold-badge-icon`} small variant="gold" />
                  )}
                </NavLink>
              );
            })}
          </nav>
        </aside>
  );
}

export { Brand, MobileMoreMenu, CustomerBottomNav, CustomerNavRow, TeamMobileNav, TeamSidebar };
